import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Session } from "@/types/profile";
import { Laptop, MapPin, Clock, ArrowRightFromLine, Power } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { getIcon, parseUserAgent, formatTimestamp, fetchIpLocation } from "@/utils/sessionHelpers";

interface SessionListProps {
    sessions: Session[];
    onRevokeSession: (sessionId: string) => void;
    onRevokeAllOtherSessions: () => void;
}

export function SessionList({ sessions, onRevokeSession, onRevokeAllOtherSessions }: SessionListProps) {
    const [ipLocations, setIpLocations] = useState<Record<string, string>>({});
    const fetchedIpsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const fetchLocations = async () => {
            const uniqueIps = [...new Set(
                sessions
                    .filter(s => {
                        const ip = s.ip?.split(',')[0]?.trim();
                        return ip &&
                            !s.location &&
                            !fetchedIpsRef.current.has(ip);
                    })
                    .map(s => s.ip?.split(',')[0]?.trim())
                    .filter(Boolean) as string[]
            )];

            if (uniqueIps.length === 0) return;

            uniqueIps.forEach(ip => fetchedIpsRef.current.add(ip));

            const locationPromises = uniqueIps.map(async (ip) => {
                const location = await fetchIpLocation(ip);
                return { ip, location };
            });

            const results = await Promise.all(locationPromises);

            const newLocations: Record<string, string> = {};
            results.forEach(({ ip, location }) => {
                if (location) {
                    newLocations[ip] = location;
                }
            });

            if (Object.keys(newLocations).length > 0) {
                setIpLocations(prev => ({ ...prev, ...newLocations }));
            }
        };

        if (sessions.length > 0) {
            fetchLocations();
        }
    }, [sessions]);

    return (
        <div className="animate-fade-in max-w-[1200px] space-y-8">
            <PageHeader
                icon={Laptop}
                title="Sessions"
                description="Manage your active login sessions"
                action={
                    sessions.length > 1 ? (
                        <Button
                            variant="outline"
                            className="text-destructive border-destructive/20 hover:bg-destructive/10"
                            onClick={onRevokeAllOtherSessions}
                        >
                            Revoke All Other Sessions
                        </Button>
                    ) : undefined
                }
            />
            <div className="space-y-4">
                {sessions.length === 0 ? (
                    <Card className="glass-card p-12 text-center">
                        <Laptop className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <p className="text-muted-foreground">No active sessions found</p>
                    </Card>
                ) : (
                    sessions.map((session) => {
                        const Icon = getIcon(session.ua);
                        const uaDetails = parseUserAgent(session.ua);
                        const trimmedIp = session.ip?.split(',')[0]?.trim() || '';

                        return (
                            //sticky top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10
                            <Card
                                key={session.id}
                                className="glass-card p-3 sm:p-4 transition-all hover:shadow-md"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                                        </div>
                                        <div className="space-y-0.5 flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-sm sm:text-base">{uaDetails.device}</h3>
                                                {session.is_current && (
                                                    <Badge className="bg-cyan-500 hover:bg-cyan-600 text-white border-0 text-[10px] px-1.5 py-0 h-5">
                                                        Current
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs sm:text-sm text-muted-foreground">{uaDetails.browser}</p>
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1.5">
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3 shrink-0" />
                                                    <span className="truncate">{session.location || ipLocations[trimmedIp] || "Unknown Location"}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3 shrink-0" />
                                                    <span>{formatTimestamp(session.mt)}</span>
                                                </div>
                                            </div>

                                            <div className="text-[10px] sm:text-xs font-mono text-muted-foreground/70 mt-1">
                                                IP: <span className="text-foreground/50">{session.ip?.split(',')[0]}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {!session.is_current && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-10 w-10 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                                                        onClick={() => onRevokeSession(session.id)}
                                                    >
                                                        <Power className="w-5 h-5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Revoke Session</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </Card>
                        );
                    })
                )}
            </div>
        </div>
    );
}
